import React from 'react';
import { MapPin } from 'lucide-react';
import { FormField, AddressAutocomplete } from '../../../components';

// Step navigation lives in the ConvertClient page header, top-right.
const ContactAddressInfo = ({ updateData, data }) => {
    const contact = data.contactDetails || {};

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Typing the address by hand invalidates any pin we previously resolved,
        // so clear it — the backend then re-geocodes from the new text on save.
        const patch = { [name]: value };
        if (name === 'address_line_1' || name === 'address_line_2'
            || name === 'city' || name === 'postcode' || name === 'country') {
            patch.latitude = null;
            patch.longitude = null;
        }
        updateData('contactDetails', patch);
    };

    // A Places suggestion was chosen: fill in whatever Google resolved (city,
    // postcode, country…) plus the lat/lng the carer's distance is measured from.
    const handleAddressResolved = (fields) => {
        updateData('contactDetails', fields);
    };

    const hasPin = contact.latitude != null && contact.longitude != null;

    return (
        <div>
            <h2 className="text-[22px] font-bold text-[#334155] dark:text-gray-200 mb-8">Contact & Address Info</h2>

            <div className="flex flex-col md:flex-row gap-10">

                {/* Left Column - Client's Previous/Home Address */}
                <div className="flex-1 flex flex-col gap-6">
                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">Client Home Address</h3>

                    <AddressAutocomplete
                        label="Address Line 1"
                        name="address_line_1"
                        value={contact.address_line_1 || ''}
                        onChange={handleChange}
                        onResolve={handleAddressResolved}
                        placeholder="Start typing an address…"
                    />
                    <FormField
                        label="Address Line 2"
                        name="address_line_2"
                        value={contact.address_line_2 || ''}
                        onChange={handleChange}
                        placeholder="Apartment, suite, etc. (Optional)"
                    />

                    {/* Confirms the address resolved to a map pin — that pin is
                        what a carer's check-in distance is measured against. */}
                    {hasPin && (
                        <p className="-mt-3 text-[12px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                            <MapPin size={13} />
                            Location pinned ({Number(contact.latitude).toFixed(5)}, {Number(contact.longitude).toFixed(5)})
                        </p>
                    )}

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="City / Town"
                            name="city"
                            value={contact.city || ''}
                            onChange={handleChange}
                            placeholder="Enter city"
                        />
                        <FormField 
                            label="County / State"
                            name="county_state"
                            value={contact.county_state || ''}
                            onChange={handleChange}
                            placeholder="Enter county"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="Postcode"
                            name="postcode"
                            value={contact.postcode || ''}
                            onChange={handleChange}
                            placeholder="e.g. SW1A 1AA"
                        />
                        <FormField 
                            label="Country"
                            name="country"
                            value={contact.country || ''}
                            onChange={handleChange}
                            placeholder="e.g. United Kingdom"
                        />
                    </div>

                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200 mt-4">Client Contact Numbers</h3>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="Home Phone"
                            name="home_phone"
                            value={contact.home_phone || ''}
                            onChange={handleChange}
                            placeholder="e.g. 020 1234 5678"
                        />
                        <FormField 
                            label="Mobile Phone"
                            name="mobile_phone"
                            value={contact.mobile_phone || ''}
                            onChange={handleChange}
                            placeholder="e.g. 07123 456789"
                        />
                    </div>

                    <FormField 
                        label="Personal Email Address"
                        name="personal_email"
                        type="email" 
                        value={contact.personal_email || ''}
                        onChange={handleChange}
                        placeholder="name@example.com"
                    />
                </div>

                {/* Vertical Dashed Divider */}
                <div className="hidden md:block w-px border-l-2 border-dashed border-gray-200 dark:border-gray-800"></div>

                {/* Right Column - Next of Kin & Emergency Contacts */}
                <div className="flex-1 flex flex-col gap-6">
                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200">Primary Next of Kin (Emergency Contact)</h3>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="Full Name"
                            name="nok_full_name"
                            value={contact.nok_full_name || ''}
                            onChange={handleChange}
                            placeholder="Enter NOK name"
                            required
                        />
                        <FormField 
                            label="Relationship"
                            name="nok_relationship"
                            value={contact.nok_relationship || ''}
                            onChange={handleChange}
                            placeholder="e.g. Daughter"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="Contact Number"
                            name="nok_contact_number"
                            value={contact.nok_contact_number || ''}
                            onChange={handleChange}
                            placeholder="Phone number"
                            required
                        />
                        <FormField 
                            label="Email Address"
                            name="nok_email"
                            type="email" 
                            value={contact.nok_email || ''}
                            onChange={handleChange}
                            placeholder="email@example.com"
                        />
                    </div>

                    <FormField 
                        label="Power of Attorney Status"
                        name="nok_poa_status"
                        type="select"
                        value={contact.nok_poa_status || ''}
                        onChange={handleChange}
                        placeholder="Select POA Status"
                        options={[
                            { label: 'None', value: 'None' },
                            { label: 'Health & Welfare', value: 'Health & Welfare' },
                            { label: 'Property & Finance', value: 'Property & Finance' },
                            { label: 'Both', value: 'Both' }
                        ]}
                    />

                    <h3 className="text-[15px] font-semibold text-[#1E293B] dark:text-gray-200 mt-4">Secondary Contact (Optional)</h3>

                    <div className="grid grid-cols-2 gap-5">
                        <FormField 
                            label="Full Name"
                            name="sec_contact_full_name"
                            value={contact.sec_contact_full_name || ''}
                            onChange={handleChange}
                            placeholder="Enter contact name"
                        />
                        <FormField 
                            label="Relationship"
                            name="sec_contact_relationship"
                            value={contact.sec_contact_relationship || ''}
                            onChange={handleChange}
                            placeholder="e.g. Son"
                        />
                    </div>
                    <FormField 
                        label="Contact Number"
                        name="sec_contact_number"
                        value={contact.sec_contact_number || ''}
                        onChange={handleChange}
                        placeholder="Phone number"
                    />
                </div>

            </div>

        </div>
    );
};

export default ContactAddressInfo;